import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Grid, Typography, Box } from '@material-ui/core';

import { selectItem, saveTreadItem, clearSaveStatus } from '../item/itemSlice';
import { enqueueSnackbar } from '../snackbar/snackbarSlice';
import { selectGlobals } from '../app/appSlice';

import FilePicker from '../common/filePicker';
import { BooleanField, TextField } from '../common/fields';
import ActionBar from '../item/ActionBar';
import DeleteList from '../common/deleteList';
import MainImage from '../itemFamily/MainImage';

import { defaultStudioFolderPC24 } from '../../constants';

const defaultStudioFolder = defaultStudioFolderPC24;

const TreadItemPagePC24 = (props) => {
  const { itemOwnerId, itemId } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { canEdit: userAllowedToEdit } = props;
  const { editableFields, item, saveStatus, saveError } =
    useSelector(selectItem);
  const { attributes } = useSelector(selectGlobals);
  const [itemData, setItemData] = useState(item);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    // Show feedback on save status
    if (saveStatus === 'failed') {
      dispatch(
        enqueueSnackbar({
          message: t(`errors:${saveError.message}`),
          options: {
            variant: 'error',
          },
        })
      );
      dispatch(clearSaveStatus());
    } else if (saveStatus === 'succeeded') {
      dispatch(
        enqueueSnackbar({
          message: t('common:savedSuccessfully'),
          options: {
            variant: 'success',
          },
        })
      );
      dispatch(clearSaveStatus());
      setHasUnsavedChanges(false);
    }
  }, [saveStatus]);

  const fieldIsDisabled = (field) => {
    if (userAllowedToEdit && editableFields.includes(field)) {
      return false;
    }

    return true;
  };

  const handleChange = (field, value) => {
    setItemData({
      ...itemData,
      [field]: value,
    });
    setHasUnsavedChanges(true);
  };

  const handleFieldChange = (event) => {
    handleChange(event.target.name, event.target.value);
  };

  const filterCountryExclusions = () => {
    return itemData.itemFamilyMarkets.map((country) => ({
      value: country,
      excluded: itemData.excludeMarkets.includes(country),
      label: t(`countries:${country}`),
    }));
  };

  const filterAttributeExclusions = (familyField, itemField, globalField) => {
    const itemValues = itemData[familyField];

    return itemValues.map((attribute) => {
      let label = attribute;

      if (attributes[globalField]) {
        const row = attributes[globalField].find(
          (item) => item.id === attribute
        );
        if (row) {
          label = row.global_name;
        }
      }

      return {
        value: attribute,
        label: label,
        excluded: itemData[itemField].includes(attribute),
      };
    });
  };

  const handleDeleteListItem = (field, item) => {
    const newState = item.excluded
      ? itemData[field].filter((i) => i !== item.value)
      : itemData[field].concat(item.value);
    handleChange(field, newState);
  };

  const cancelChanges = () => {
    setItemData(item);
    setHasUnsavedChanges(false);
  };

  const saveChanges = () => {
    // Save to server
    dispatch(saveTreadItem({ itemOwnerId, itemId, itemData }));
  };

  return (
    <>
      <Grid container spacing={7}>
        <Grid item xs={12} md={8}>
          <Box mb={2}>
            <Typography variant="subtitle1">{t('item:itemName')}</Typography>
            <Typography variant="h4" component="h1">
              {item.itemName}
            </Typography>
          </Box>

          <Box mb={3}>
            <Typography variant="subtitle1">{t('item:itemCode')}</Typography>
            <Typography variant="h5">{item.itemCode}</Typography>
          </Box>

          <Box mb={4}>
            <Typography variant="overline" display="block">
              {t('item:lastChanged')}
            </Typography>
            <Typography variant="caption">{item.updated}</Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={6}>
              <TextField
                label={t('item:itemFamily')}
                name="itemFamilyName"
                value={itemData.itemFamilyName}
                onChange={handleFieldChange}
                disabled={fieldIsDisabled('itemFamilyName')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t('item:ean')}
                name="eanCode"
                value={itemData.eanCode}
                onChange={handleFieldChange}
                disabled={fieldIsDisabled('eanCode')}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label={t('item:itemLifeCycle')}
                name="ean"
                value={itemData.itemLifeCycle}
                onChange={handleFieldChange}
                disabled={fieldIsDisabled('itemLifeCycle')}
              />
            </Grid>
          </Grid>

          <Box my={5}>
            <Box mb={3}>
              <Typography variant="h6" component="h2">
                {t('itemFamily:markets')}
              </Typography>
            </Box>
            <DeleteList
              items={filterCountryExclusions()}
              onDelete={(item) => handleDeleteListItem('excludeMarkets', item)}
              disabled={fieldIsDisabled('excludeMarkets')}
            />
          </Box>

          <Box my={3}>
            <Box mb={3}>
              <Typography variant="h6" component="h2">
                {t('itemFamily:attributes')}
              </Typography>
            </Box>
            <DeleteList
              items={filterAttributeExclusions(
                'itemFamilyAttributes',
                'excludeAttributes',
                'attributes'
              )}
              onDelete={(item) =>
                handleDeleteListItem('excludeAttributes', item)
              }
              disabled={fieldIsDisabled('excludeAttributes')}
            />
          </Box>

          <Box my={5}>
            <Box mb={3}>
              <Typography variant="h6" component="h2">
                {t('item:certificateInfo')}
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextField
                  label={t('item:loadCapacityComment')}
                  name="loadCapacityComment"
                  value={itemData.loadCapacityComment || ''}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('loadCapacityComment')}
                  multiline={true}
                  helperText={t('item:loadCapacityCommentHelp')}
                />
              </Grid>
            </Grid>
          </Box>

          <Box my={5}>
            <Box mb={3}>
              <Typography variant="h6" component="h2">
                {t('item:technicalInfo')}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextField
                  label={`${t('item:width')} (mm)`}
                  name="width"
                  value={itemData.width}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('width')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`${t('item:measuredLength')} (mm)`}
                  name="measuredLength"
                  value={itemData.measuredLength}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('measuredLength')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`${t('item:measuredWeight')} (kg)`}
                  name="measuredWeight"
                  value={itemData.measuredWeight}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('measuredWeight')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`${t('item:weightPerMeter')} (kg/m)`}
                  name="weightPerMeter"
                  value={itemData.weightPerMeter}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('weightPerMeter')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`${t('item:treadDepth')} (mm)`}
                  name="treadDepth"
                  value={itemData.treadDepth}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('treadDepth')}
                />
              </Grid>
            </Grid>
          </Box>

          <Box my={5}>
            <Box mb={3}>
              <Typography variant="h6" component="h2">
                {t('item:euLabelInfo')}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextField
                  label={t('item:euDirectiveNumber')}
                  name="eUDirectiveNumberCode"
                  value={itemData.eUDirectiveNumberCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('eUDirectiveNumberCode')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={t('item:wetGrip')}
                  name="wetGripCode"
                  value={itemData.wetGripCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('wetGripCode')}
                />
              </Grid>
              <Grid item xs={6}>
                <BooleanField
                  label={t('item:snowGrip')}
                  name="snowGripCode"
                  value={itemData.snowGripCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('snowGripCode')}
                />
              </Grid>
              <Grid item xs={6}>
                <BooleanField
                  label={t('item:iceGrip')}
                  name="iceGripCode"
                  value={itemData.iceGripCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('iceGripCode')}
                />
              </Grid>
            </Grid>

            <Box my={5}>
              <TextField
                label={t('item:applicableProductSizes')}
                name="applicableProductSizes"
                value={itemData.applicableProductSizes.split(',').join(', ')}
                onChange={handleFieldChange}
                disabled={fieldIsDisabled('applicableProductSizes')}
              />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <MainImage
            src={itemData.imageMain?.medium || itemData.itemFamilyImage?.medium}
          />
          <FilePicker
            fieldName={t('item:overrideImageMain')}
            fieldValue={itemData.imageMain}
            setFieldValue={(file) => handleChange('imageMain', file)}
            disabled={fieldIsDisabled('imageMain')}
            rootFolderId={defaultStudioFolder}
          />
          <FilePicker
            viewOnly={true}
            fieldName={t('item:certificate')}
            fieldValue={itemData.certificate}
          />
          <FilePicker
            viewOnly={true}
            fieldName={`${t('item:certificate')} (${t('item:imperial')})`}
            fieldValue={itemData.certificateImperial}
          />
        </Grid>
      </Grid>

      {hasUnsavedChanges ? (
        <ActionBar
          cancelChanges={cancelChanges}
          saveChanges={saveChanges}
          disabled={saveStatus === 'loading'}
        />
      ) : null}
    </>
  );
};

TreadItemPagePC24.propTypes = {
  canEdit: PropTypes.bool,
};

export default TreadItemPagePC24;
